'use client';
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from '@/app/i18n/client';
import languageContext from '@/context/language';
import { FavoritesTable } from './favorites-list-table';
import { Checkbox, GlobalLoading, Pagination, Button, Input } from '@/shared/components';
import { syncPrice } from '../../utils/price-availability';
import { useMutationDeleteFav, useQueryFavoritesList } from '@/hooks/favorites.hook';
import { useAppStore } from '@/store/app-store';
import { SpinnerBox } from '@/shared/pageComponents/spinner-box';
import { AddCartFailMessage } from '@/shared/pageComponents/add-cart-fail-message';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { usePriceStore } from '../../../store/price-store';
import { getProductPrice } from '@/shared/tracking/utils';
import { usePlacement } from '@/shared/tracking/use-tracking-helpers';
import { isEmpty } from 'lodash';
import { scrollToTop } from '@/app/utils';
import { PriceErrorMessage } from '@/shared/pageComponents/price-error-message';
import { ChildCatalogs } from '@/types/product-detail';

export default function Favorites() {
  const { lang } = useContext(languageContext);
  const { t } = useTranslation(lang, 'favorites');
  const [pageSize, setPageSize] = useState<number>(15);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [favListData, setFavListData] = useState<any>();
  const [selected, setSelected] = useState({});
  const deleteFav = useMutationDeleteFav();
  const [rowSelection, setRowSelection] = React.useState({});
  const [search, setSearch] = useState('');
  const [searchStr, setSearchStr] = useState<number | string>();
  const [parentClass, setParentClass] = useState<string>('mt-4 col-span-2 w-full flex flex-col');
  const [addCartLoading, setAddCartLoading] = useState(false);
  const [addCartFailMessage, setAddCartFailMessage] = useState([]);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [priceFailSkus, setPriceFailSkus] = useState([]);
  const { shipToList, initialed } = useAppStore();
  const { productPrice } = usePriceStore();
  const { data, isLoading, isFetching, refetch } = useQueryFavoritesList({
    pageSize: pageSize,
    pageNumber: pageNumber,
    simpleSearchStr: searchStr,
  });

  // tracking data
  const { trackEvent } = useEventTracking();
  const placement = usePlacement();

  useEffect(() => {
    if (isLoading || isFetching) {
      GlobalLoading.show();
    } else {
      GlobalLoading.hide();
    }
  }, [isLoading, isFetching]);
  useEffect(() => {
    if (!initialed) return;
    if (data) {
      setFavListData(data);
      const childCatalogs = data.data?.map((e) => {
        return {
          childCatalogNumber: e?.catalogNumber || '',
          webCatalogNumber: e?.webCatalogNumber || '',
        } as ChildCatalogs;
      });
      syncPrice(childCatalogs).then((res) => {
        setPriceFailSkus(res.failList);
      });
      if (data.data.length > 0) {
        setParentClass('mt-4 border border-solid border-gray-450 rounded col-span-2 w-full flex flex-col');
      }
    }
  }, [data, shipToList, initialed]);
  useEffect(() => {
    setIsIndeterminate(Object.keys(rowSelection).length > 0 && Object.keys(rowSelection).length < data.data.length);
  }, [rowSelection]);
  const selectFunc = (props) => {
    setSelected(props);
  };
  const removeFunc = () => {
    setRowSelection({});
    setSelected({});
    refetch();
  };
  const rowSelectionFunc = (props) => {
    setRowSelection(props);
  };
  const onSelectAll = (checked: boolean) => {
    if (checked) {
      const obj = {};
      for (let i = 0; i < data.data.length; i++) {
        obj[i] = true;
      }
      setSelected(obj);
      setRowSelection(obj);
    } else {
      setRowSelection({});
    }
  };
  const handlePageClick = (page) => {
    setPageNumber(page);
  };
  const getSelectedSKUs = () => {
    const keys = Object.keys(selected);
    return keys.map((key) => {
      return data.data[key].catalogNumber;
    });
  };
  const getSelectedProducts = () => {
    const keys = Object.keys(selected);
    return keys.map((key) => {
      return data.data[key];
    });
  };
  const removeSelected = () => {
    const catalogNumbers = getSelectedSKUs();

    GlobalLoading.show();
    deleteFav.mutateAsync(catalogNumbers).then((response) => {
      if (response.code === '200') {
        removeFunc();
        // tracking remove from favorites list
        trackEvent({
          key: 'eventsData',
          action: 'removeFromFavoritesList',
          payload: {
            event_name: 'removeFromFavoritesList',
            contentType: 'Product',
            products: catalogNumbers?.map((item) => {
              return {
                catalogId: item,
                placement,
              };
            }),
          },
        });
      }
    });
  };
  const addToCartSelected = async () => {
    const catalogNumbers = getSelectedSKUs();
    const productArr = [];
    catalogNumbers.forEach((sku, index) => {
      const obj = { catalogNumber: sku, quantity: 1 };
      productArr.push(obj);
    });
    setAddCartLoading(true);
    const res = await window.miniCart?.addProductsToCart(productArr);
    // add to cart event tracking
    const productData = getSelectedProducts();
    trackEvent({
      key: 'cart',
      action: 'addToCart',
      payload: {
        products: productData.map((item) => {
          return {
            name: item?.catalogName,
            catalogId: item?.catalogNumber,
            quantity: 1,
            price: getProductPrice(productPrice[item.catalogNumber]),
            uom: item?.unitSize,
            placement,
            method: 'favorites commerce-box',
          };
        }),
      },
    });
    setAddCartFailMessage(res?.data?.failWithMessage);
    setAddCartLoading(false);
    if (!isEmpty(res?.data?.failWithMessage)) {
      scrollToTop();
    }
  };
  const handleSearchEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchFavList();
    }
  };
  const searchFavList = () => {
    setPageNumber(1);
    setSearchStr(search?.trim());
  };
  return (
    <>
      <AddCartFailMessage failList={addCartFailMessage} onClose={() => setAddCartFailMessage([])} />
      <PriceErrorMessage failSkus={priceFailSkus} onClose={() => setPriceFailSkus([])} />
      <div className="grid grid-cols-[1fr_345px] w-full custom-favorite">
        <div className="col-span-2 flex mt-8 w-full mb-8 sm:inline-flex sm:flex-col">
          <div className="items-baseline gap-x-4 w-full">
            <h1 className="font-medium">{t('favorite')}</h1>
          </div>
          <div className="relative text-gray-600 ml-auto flex sm:row-start-2 sm:ml-0 lg:w-[35%] xl:w-[35%] 2xl:w-[35%]">
            <Input
              className="bg-white h-10 text-sm w-full"
              value={search}
              placeholder={t('search.placeholder')}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                handleSearchEnterKey(e);
              }}
            />
            <Button className="right-0 top-0 mini box-border rounded-none rounded-r relative" onClick={searchFavList}>
              <i
                className=" icon-24-search-mono text-white-600 absolute left-2/4 top-2/4"
                style={{ transform: 'translate(-50%, -50%)' }}
              ></i>
            </Button>
          </div>
        </div>
        {favListData && (
          <div className={parentClass}>
            <FavoritesTable
              data={favListData.data}
              selectFunc={selectFunc}
              removeFunc={removeFunc}
              rowSelection={rowSelection}
              setRowSelection={rowSelectionFunc}
            />
            {favListData.data && favListData.data.length > 0 && (
              <div
                className={`grid grid-cols-2 items-center print:hidden border-0 border-t border-solid border-gray-300 pr-4`}
              >
                <div className="flex items-center sm:flex-col sm:items-start pl-4">
                  <Checkbox
                    checked={Object.keys(rowSelection).length > 0}
                    label={<span className="text-info">{t('all')}</span>}
                    className="flex-shrink-0 mb-0"
                    onChange={onSelectAll}
                    indeterminate={isIndeterminate}
                  />
                  <div className="sm:flex sm:flex-col sm:gap-y-2 sm:items-start sm:mt-4">
                    <div className="flex">
                      <Button
                        type="link"
                        className="p-0 h-auto leading-none ml-8 sm:ml-0 text-sm font-normal disabled:cursor-not-allowed disabled:bg-transparent disabled:border-none disabled:text-[#A5A5AE] flex-shrink-0"
                        onClick={removeSelected}
                        disabled={Object.keys(rowSelection).length === 0}
                      >
                        {t('delete.selected.items')}
                      </Button>
                      <div className="flex p-0 h-auto leading-none text-sm font-normal disabled:cursor-not-allowed disabled:bg-transparent disabled:border-none disabled:text-[#A5A5AE] mx-4 sm:mx-0 flex-shrink-0">
                        <SpinnerBox isLoading={addCartLoading}>
                          <Button
                            type="link"
                            className="p-0 h-auto leading-none text-sm font-normal disabled:cursor-not-allowed disabled:bg-transparent disabled:border-none disabled:text-[#A5A5AE] mx-4 sm:mx-0 flex-shrink-0"
                            onClick={addToCartSelected}
                            disabled={Object.keys(rowSelection).length === 0}
                          >
                            {t('add.all.cart')}
                          </Button>
                        </SpinnerBox>
                      </div>
                    </div>
                  </div>
                </div>
                <Pagination
                  pageSize={pageSize}
                  currentPage={favListData.page.pageNo}
                  onPageChange={handlePageClick}
                  total={favListData.page.count}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
